import {
  DataGrid,
  GridCellValue,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { Button, CircularProgress, Grid } from '@mui/material';
import React, { useState } from 'react';
import { Item } from '../../utils/sharedTypes';
import { useFindAllRmasQuery } from '../../api/api-hooks';
import { Rma } from '../../api/api-generated';
import LptRmaCommentsDialog from '../lpt-rma/LptRmaCommentsDialog';
import LptRmaPartsDialog from '../lpt-rma/LptRmaPartsDialog';
import { filterRmas } from './rmaHistoryUtils';
import { RmaHistoryItemFilter } from './rmaHistoryTypes';
import { truncateISOString } from '../../utils/dateTime';
import { useHardwareTypeMap } from '../../hooks/useHardwareTypeMap';
import { useGridSort } from '../../hooks/useGridSort';

interface Props {
  setSelectedRmas: React.Dispatch<React.SetStateAction<Item[]>>;
  filters: RmaHistoryItemFilter;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const LptRmaHistoryTable = ({
  setSelectedRmas,
  filters,
  setEditModalOpenFlag,
}: Props) => {
  const { data: rmas } = useFindAllRmasQuery({});
  const { sortModel, handleSortModelChange } = useGridSort('shipDate', 'desc');
  const [clickedRma, setClickedRma] = useState<string | number>(0);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState<boolean>(false);
  const [partsDialogOpen, setPartsDialogOpen] = useState<boolean>(false);
  const typeMap = useHardwareTypeMap();
  if (rmas === undefined || typeMap === undefined)
    return <CircularProgress></CircularProgress>;
  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'nameId', headerName: 'RMA #', width: 180 },
    { field: 'company', headerName: 'Company Name', width: 180 },
    { field: 'serial', headerName: 'Serial', width: 120 },
    { field: 'analyzerType', headerName: 'Hardware Type', width: 200 },
    { field: 'status', headerName: 'Ship Status', width: 150 },
    { field: 'shipDate', headerName: 'Ship Date', width: 150 },
    { field: 'shipper', headerName: 'Shipping Co.', width: 160 },
    { field: 'trackingNumber', headerName: 'Tracking #', width: 150 },
    {
      field: 'subfields',
      headerName: ' ',
      width: 250,
      renderCell: (params) => {
        const openComments = (event: React.MouseEvent) => {
          event.stopPropagation();
          const rmaId: GridCellValue = params.getValue(params.id, 'id');
          if (typeof rmaId === 'number' || typeof rmaId === 'string')
            setClickedRma(rmaId);
          setCommentsDialogOpen(true);
        };
        const openParts = (event: React.MouseEvent) => {
          event.stopPropagation();
          const rmaId: GridCellValue = params.getValue(params.id, 'id');
          if (typeof rmaId === 'number' || typeof rmaId === 'string')
            setClickedRma(rmaId);
          setPartsDialogOpen(true);
        };
        return (
          <Grid container direction='row' spacing={1}>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={openComments}>
                Comments
              </Button>
            </Grid>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={openParts}>
                Parts
              </Button>
            </Grid>
          </Grid>
        );
      },
    },
  ];
  const rows = rmas
    // Only display completed and filtered RMAs
    .filter((item: Rma) =>
      item.isComplete ? filterRmas(item, filters) : false
    )
    .map((item: Rma) => ({
      ...item,
      company: item.company.companyName,
      serial: item.hardware.serial,
      analyzerType: typeMap.get(item.hardware.hardwareTypeId),
      shipDate: item.shipDate ? truncateISOString(item.shipDate) : '',
    }));
  const handleRowClick = (params: GridRowParams) => {
    setSelectedRmas(rmas.filter((rma: Rma) => rma.id === params.row.id));
    setEditModalOpenFlag(true);
  };
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedRmas(rmas.filter((rma: Rma) => model.includes(rma.id)));
  };
  return (
    <>
      <LptRmaCommentsDialog
        clickedRma={clickedRma}
        commentsDialogOpen={commentsDialogOpen}
        setCommentsDialogOpen={setCommentsDialogOpen}
      />
      <LptRmaPartsDialog
        dialogOpen={partsDialogOpen}
        setDialogOpen={setPartsDialogOpen}
        clickedRma={clickedRma}
      />
      <div style={{ paddingBottom: '1rem', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={100}
          checkboxSelection
          disableSelectionOnClick
          autoHeight
          disableColumnMenu={true}
          onSelectionModelChange={handleTableSelection}
          onSortModelChange={handleSortModelChange}
          onRowClick={handleRowClick}
          sortModel={sortModel}
        />
      </div>
    </>
  );
};

export default LptRmaHistoryTable;
